import React, { useRef, useState, useEffect } from "react";
import { Autocomplete } from "@react-google-maps/api";
import { FiMapPin } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { notifyError, notifySuccess } from "./ErrorAlert";
import { HEADER_MESSAGES } from "../constants/headerMessages";

const AddressModal = ({
  isOpen,
  mode,
  address: initialAddress,
  token,
  onClose,
  onSaved,
  onDelete,
}) => {
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);

  const addressAutocompleteRef = useRef(null);

  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!isOpen) return;

    setAddress(isEditMode ? initialAddress?.address || "" : "");
  }, [isOpen, isEditMode, initialAddress]);

  if (!isOpen) {
    return null;
  }

  const handlePlaceChanged = () => {
    const place = addressAutocompleteRef.current?.getPlace();

    const formattedAddress = place?.formatted_address || "";

    if (formattedAddress) {
      setAddress(formattedAddress);
    }
  };

  const handleSaveAddress = async () => {
    if (!address.trim()) {
      notifyError(HEADER_MESSAGES.ADDRESS_EMPTY);
      return;
    }

    if (!token) {
      notifyError(
        isEditMode
          ? HEADER_MESSAGES.UPDATE_ADDRESS_FAILED
          : HEADER_MESSAGES.SAVE_ADDRESS_FAILED,
      );
      return;
    }

    try {
      setSaving(true);

      const response = await fetch(
        isEditMode
          ? `${process.env.REACT_APP_API_URL}/profile/saved-addresses/${initialAddress.id}`
          : `${process.env.REACT_APP_API_URL}/profile/saved-addresses`,
        {
          method: isEditMode ? "PUT" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            address: address.trim(),
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
            (isEditMode
              ? HEADER_MESSAGES.UPDATE_ADDRESS_FAILED
              : HEADER_MESSAGES.SAVE_ADDRESS_FAILED),
        );
      }

      const updatedAddresses = Array.isArray(data.savedAddresses)
        ? data.savedAddresses
        : [];

      notifySuccess(
        data.message ||
          (isEditMode
            ? HEADER_MESSAGES.ADDRESS_UPDATED
            : HEADER_MESSAGES.ADDRESS_SAVED),
      );

      onSaved(updatedAddresses);

      setAddress("");
      onClose();
    } catch (err) {
      console.error(
        isEditMode ? "Failed to update address:" : "Failed to save address:",
        err,
      );

      notifyError(
        err.message ||
          (isEditMode
            ? HEADER_MESSAGES.UPDATE_ADDRESS_FAILED
            : HEADER_MESSAGES.SAVE_ADDRESS_FAILED),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;

    setAddress("");
    onClose();
  };

  const handleDelete = () => {
    if (saving || !isEditMode) return;

    onDelete();
  };

  return (
    <div className="edit-modal-overlay" onClick={handleClose}>
      <div
        className="edit-individual-modal edit-address-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="edit-modal-header">
          <div className="edit-modal-title-section">
            <div className="edit-modal-icon">
              <FiMapPin />
            </div>

            <div>
              <h2>{isEditMode ? "Edit Address" : "Add Address"}</h2>

              <p>
                {isEditMode
                  ? "Update your saved address."
                  : "Save an address for quick access later."}
              </p>
            </div>
          </div>

          <button
            type="button"
            className="edit-modal-close"
            onClick={handleClose}
            disabled={saving}
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="edit-modal-body edit-modal-address-body">
          <div className="edit-form-group">
            <label>Address</label>

            <div className="edit-address-input-row">
              <Autocomplete
                onLoad={(auto) => {
                  addressAutocompleteRef.current = auto;
                }}
                onPlaceChanged={handlePlaceChanged}
                options={{
                  fields: ["formatted_address"],
                }}
              >
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Enter Address"
                  disabled={saving}
                />
              </Autocomplete>

              {isEditMode && (
                <button
                  type="button"
                  className="edit-address-delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                  title="Delete Address"
                >
                  <MdDelete />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="edit-modal-footer">
          <div></div>

          <div className="edit-footer-right">
            <button
              type="button"
              className="edit-cancel-button"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="button"
              className="edit-update-button"
              onClick={handleSaveAddress}
              disabled={saving}
            >
              {saving
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update Address"
                  : "Save Address"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressModal;
